import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/vercel/path0/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "why-use-encodable"
    }}>{`Why use Encodable?`}</h1>
    <h2 {...{
      "id": "motivation"
    }}>{`Motivation`}</h2>
    <blockquote>
      <p parentName="blockquote">{`Developing reusable components with consistent APIs is challenging.`}</p>
    </blockquote>
    <p>{`Each component ends up having very different API.
If you develop a word cloud component, how would you let user specify the `}<inlineCode parentName="p">{`fontSize`}</inlineCode>{`, `}<inlineCode parentName="p">{`color`}</inlineCode>{`,
`}<inlineCode parentName="p">{`text`}</inlineCode>{` etc.?`}</p>
    <p>{`One common approach is to accept accessor functions as arguments, but then you punt most of the
implementation responsibilities to the library consumer. The configuration is also not serializable.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`{
  "fontSize": d => scale(d) // you have to setup a scale, set its domain, make domain start at 0, etc.
}
`}</code></pre>
    <p>{`Then there are alternatives such as exposing a number of arbitrarily chosen fields, e.g.
`}<inlineCode parentName="p">{`fontSizeField`}</inlineCode>{`, `}<inlineCode parentName="p">{`fontSizeRange`}</inlineCode>{`, which you have to handle inside the component. If you start from
expecting `}<inlineCode parentName="p">{`fontSize`}</inlineCode>{` to be using linear scale and later want to support log scale, you may have to
expose new field `}<inlineCode parentName="p">{`fontSizeScaleType`}</inlineCode>{` and include new logic for creating log scale.`}</p>
    <p>{`Later on, if you start developing a suite of components, you either have to come up with a list of
common properties or naming conventions, so all of your visualization components at least work
similarly. After all, this is likely to be yet another standard that only applies to your
components.`}</p>
    <h2 {...{
      "id": "inspiration"
    }}>{`Inspiration`}</h2>
    <p>{`This library was heavily inspired by `}<a parentName="p" {...{
        "href": "https://github.com/vega/vega-lite"
      }}><inlineCode parentName="a">{`vega-lite`}</inlineCode></a>{`. `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`
gives you a grammar and rendering engine that you can use to create many different visualizations
from it. For example, this is how you create a
`}<a parentName="p" {...{
        "href": "https://vega.github.io/vega-lite/examples/bar.html"
      }}>{`bar chart`}</a>{` of population by country in
`}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "mark": "bar",
  "encoding": {
    "x": {"field": "country", "type": "ordinal"},
    "y": {"field": "population", "type": "quantitative"}
  }
}
`}</code></pre>
    <p>{`Notice how the encoding for channels `}<inlineCode parentName="p">{`x`}</inlineCode>{` and `}<inlineCode parentName="p">{`y`}</inlineCode>{` are described. See `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`'s
`}<a parentName="p" {...{
        "href": "https://vega.github.io/vega-lite/docs/encoding.html#channel-definition"
      }}>{`channel definition`}</a>{` for
full syntax explanation. For those who are less familiar with `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`, this visualization
grammar is already supported on known platforms such as `}<a parentName="p" {...{
        "href": "https://observablehq.com/"
      }}>{`Observable`}</a>{` or
Jupyter Notebook (via `}<a parentName="p" {...{
        "href": "https://altair-viz.github.io/"
      }}>{`Altair`}</a>{`, its python port).`}</p>
    <blockquote>
      <p parentName="blockquote">{`Could I develop a component which provides an API that looks like `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{` grammar?`}</p>
    </blockquote>
    <p>{`Although the grammar is very flexible and covers the definitions of the most common visualizations
already, what you can created are still limited by what `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{` renderer supports. (`}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`
has a fixed set of channels.) You hit a roadblock when you want to develop a non-traditional
component that cannot be described in `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`, or a traditional component with many subtle
details that you struggle to describe the visualization and its interactions in `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`'s
channels and grammar.`}</p>
    <h2 {...{
      "id": "enter-encodable"
    }}>{`Enter Encodable`}</h2>
    <p><inlineCode parentName="p">{`encodable`}</inlineCode>{` was created to address this need. When you already have a specific visualization in mind
and know how to build it, this library helps you `}<strong parentName="p">{`make the component "encodable"`}</strong>{` and provide
standardized API similar to `}<inlineCode parentName="p">{`vega-lite`}</inlineCode>{`'s
`}<a parentName="p" {...{
        "href": "https://vega.github.io/vega-lite/docs/encoding.html#channel-definition"
      }}>{`channel definition`}</a>{` for
consumers to define their `}<strong parentName="p">{`encoding`}</strong>{`.`}</p>
    <p>{`This is an example of how component consumers define `}<inlineCode parentName="p">{`color`}</inlineCode>{`, `}<inlineCode parentName="p">{`fontSize`}</inlineCode>{` and `}<inlineCode parentName="p">{`text`}</inlineCode>{` channels for a
word cloud component that is powered by `}<inlineCode parentName="p">{`encodable`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`{
  encoding: {
    color: {
      field: 'name',
      scale: {
        scheme: 'd3Category10',
      },
      type: 'nominal',
    },
    fontSize: {
      field: 'numberOfStudents',
      scale: { range: [0, 72] },
      type: 'quantitative',
    },
    text: {
      field: 'name',
    }
  }
}
`}</code></pre>
    <p>{`See the full
`}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/encodable-wordcloud-demo-k66ui?fontsize=14&hidenavigation=1&theme=dark"
      }}>{`example code`}</a>{`.`}</p>
    <h2 {...{
      "id": "features"
    }}>{`Features`}</h2>
    <p>{`More specifically, Encodable`}</p>
    <ul>
      <li parentName="ul">{`provides configuration for defining your own grammar for the encoding channels of your component.
The resulting grammar is a TypeScript type, which can be used to type-check the `}<em parentName="li">{`specification`}</em>{` from
component users.`}</li>
      <li parentName="ul">{`provides a parse to parse the incoming `}<em parentName="li">{`specification`}</em>{` into utilities that are useful for implementing the component as well as logic
for determining smart defaults (e.g. choosing scale type based on data type, etc.)`}</li>
      <li parentName="ul">{`does NOT render the component. It only parses incoming specification from component users into utility functions that helps you render
the visualization component.`}</li>
      <li parentName="ul">{`framework agnostic: You can use anything you want (D3, React, Vue, etc.).`}</li>
    </ul>
    <h2 {...{
      "id": "more-details"
    }}>{`More details`}</h2>
    <p>{`Please refer to `}<a parentName="p" {...{
        "href": "https://arxiv.org/abs/2009.00722"
      }}>{`the paper`}</a>{` for more details.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      